import { FC, memo, useEffect, useMemo } from 'react';
import {
  Button,
  Collapse,
  Input,
  Select,
  Skeleton,
  Typography,
  notification,
} from 'antd';
import type { CollapseProps } from 'antd';
import {
  ArrowLeftOutlined,
  SaveOutlined,
  PhoneOutlined,
  ApiOutlined,
  CheckCircleTwoTone,
  CloseCircleTwoTone,
  SettingOutlined,
  ClockCircleOutlined,
  SoundOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import Blocks from '../../Blocks/Blocks';
import blockStyles from '../../Blocks/style.module.css';
import { ScheduleTable } from './ScheduleTable/ScheduleTable';
import { ScenarioSettings } from './ScenarioSettings/ScenarioSettings';
import { useClinicSettingsDetail } from '../../../hooks/components/useClinicSettingsDetail';
import { useUtcLabel } from '../../../hooks/shared/useUtcLabel';
import { usePermissions } from '../../../hooks/shared/usePermissions';
import styles from './styles.module.css';

const { Title, Text } = Typography;

export const ClinicSettingsDetail: FC = memo(() => {
  const [api, contextHolder] = notification.useNotification();
  const { canViewCallPools } = usePermissions();

  const {
    item,
    status,
    phoneDisplay,
    handlePhoneChange,
    sipAccountId,
    setSipAccountId,
    sipAccounts,
    callPoolId,
    setCallPoolId,
    callPools,
    schedule,
    handleScheduleChange,
    handleToggleDay,
    holidays,
    handleAddHoliday,
    handleRemoveHoliday,
    scheduleLoading,
    callScheduleData,
    handleCallScheduleChange,
    handleCallToggleDay,
    callHolidays,
    handleCallAddHoliday,
    handleCallRemoveHoliday,
    callScheduleLoading,
    openPanels,
    setOpenPanels,
    saving,
    isDirty,
    handleBack,
    handleSave,
    handleDismissNotification,
    // Scenario
    scenarioType,
    setScenarioType,
    callsMaxTries,
    setCallsMaxTries,
    callsIntervalMinutes,
    setCallsIntervalMinutes,
    callsDisable,
    setCallsDisable,
    excludedDoctorIds,
    handleToggleSpecialization,
    handleToggleDoctor,
    doctorSearch,
    setDoctorSearch,
    specFilter,
    setSpecFilter,
    clinicSpecializations,
    filteredDoctors,
  } = useClinicSettingsDetail();

  useEffect(() => {
    if (status.requestSuccess) {
      api.success({
        message: status.requestSuccess,
        placement: 'bottomRight',
        icon: <CheckCircleTwoTone twoToneColor="#52c41a" />,
      });
      handleDismissNotification();
    }
    if (status.requestError) {
      api.error({
        message: status.requestError,
        placement: 'bottomRight',
        icon: <CloseCircleTwoTone twoToneColor="#ff4d4f" />,
      });
      handleDismissNotification();
    }
  }, [status.requestSuccess, status.requestError]);
  const clinicName = item?.clinic?.name ?? '—';

  const utcLabel = useUtcLabel();
  const interactionsDisabled = saving || status.responseLoading;

  const collapseItems = useMemo<CollapseProps['items']>(() => {
    const items: CollapseProps['items'] = [
      {
        key: 'basic',
        label: (
          <div className={styles.panelLabel}>
            <div className={styles.panelLabelRow}>
              <SettingOutlined className={styles.panelIcon} />
              <span className={styles.panelTitle}>Основные параметры</span>
            </div>
            <span className={styles.panelSubtitle}>
              Базовая конфигурация всех звонков
            </span>
          </div>
        ),
        children: (
          <div className={styles.panelContent}>
            <div className={styles.fieldRow}>
              <div className={styles.field}>
                <label className={styles.fieldLabel}>
                  <ApiOutlined className={styles.fieldIcon} />
                  SIP аккаунт
                </label>
                <Select
                  className={styles.fieldInputFull}
                  placeholder="Выберите SIP аккаунт"
                  value={sipAccountId}
                  onChange={(v) => setSipAccountId(v)}
                  disabled={interactionsDisabled}
                >
                  {sipAccounts.map((acc) => (
                    <Select.Option key={String(acc.id)} value={String(acc.id)}>
                      {acc.server}
                    </Select.Option>
                  ))}
                </Select>
              </div>
              <div className={styles.field}>
                <label className={styles.fieldLabel}>
                  <PhoneOutlined className={styles.fieldIcon} />
                  Номер оператора
                </label>
                <Input
                  className={styles.fieldInputFull}
                  value={phoneDisplay}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                  placeholder="Введите номер телефона"
                  disabled={interactionsDisabled}
                />
              </div>
              {canViewCallPools && (
                <div className={styles.field}>
                  <label className={styles.fieldLabel}>
                    <TeamOutlined className={styles.fieldIcon} />
                    Пул обзвона
                  </label>
                  <Select
                    className={styles.fieldInputFull}
                    placeholder="Выберите пул обзвона"
                    value={callPoolId}
                    onChange={(v) => setCallPoolId(v)}
                    disabled={interactionsDisabled}
                  >
                    {callPools.map((pool) => (
                      <Select.Option key={pool.id} value={pool.id}>
                        {pool.title}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              )}
            </div>
          </div>
        ),
      },
      {
        key: 'schedule',
        label: (
          <div className={styles.panelLabel}>
            <div className={styles.panelLabelRow}>
              <ClockCircleOutlined className={styles.panelIcon} />
              <span className={styles.panelTitle}>
                {`График работы операторов (${utcLabel})`}
              </span>
            </div>
            <span className={styles.panelSubtitle}>
              Время, когда оператор доступен для перевода абонента и которое
              робот озвучивает как рабочее время операторов
            </span>
          </div>
        ),
        children: (
          <div className={styles.panelContent} style={{ padding: 0 }}>
            <div className={styles.field}>
              <div className={styles.scheduleScroll}>
                <ScheduleTable
                  schedule={schedule}
                  holidays={holidays}
                  onScheduleChange={handleScheduleChange}
                  onToggleDay={handleToggleDay}
                  onAddHoliday={handleAddHoliday}
                  onRemoveHoliday={handleRemoveHoliday}
                  timezone={utcLabel}
                  loading={scheduleLoading}
                  disabled={interactionsDisabled}
                />
              </div>
            </div>
          </div>
        ),
      },
    ];

    items.push({
      key: 'scenario',
      label: (
        <div className={styles.panelLabel}>
          <div className={styles.panelLabelRow}>
            <SoundOutlined className={styles.panelIcon} />
            <span className={styles.panelTitle}>Настройка сценария</span>
          </div>
          <span className={styles.panelSubtitle}>
            Управление параметрами конкретного сценария
          </span>
        </div>
      ),
      children: (
        <div className={styles.panelContent} style={{ padding: 0 }}>
          <ScenarioSettings
            clinicName={clinicName}
            scenarioType={scenarioType}
            onScenarioTypeChange={setScenarioType}
            callsMaxTries={callsMaxTries}
            onCallsMaxTriesChange={setCallsMaxTries}
            callsInterval={callsIntervalMinutes}
            onCallsIntervalChange={setCallsIntervalMinutes}
            callsDisable={callsDisable}
            onCallsDisableChange={setCallsDisable}
            excludedDoctorIds={excludedDoctorIds}
            onToggleSpecialization={handleToggleSpecialization}
            onToggleDoctor={handleToggleDoctor}
            doctorSearch={doctorSearch}
            onDoctorSearchChange={setDoctorSearch}
            specFilter={specFilter}
            onSpecFilterChange={setSpecFilter}
            specializations={clinicSpecializations}
            doctors={filteredDoctors}
            utcLabel={utcLabel}
            callSchedule={callScheduleData}
            callHolidays={callHolidays}
            onCallScheduleChange={handleCallScheduleChange}
            onCallToggleDay={handleCallToggleDay}
            onCallAddHoliday={handleCallAddHoliday}
            onCallRemoveHoliday={handleCallRemoveHoliday}
            callScheduleLoading={callScheduleLoading}
            disabled={interactionsDisabled}
          />
        </div>
      ),
    });

    return items;
  }, [
    sipAccountId,
    setSipAccountId,
    sipAccounts,
    callPoolId,
    setCallPoolId,
    callPools,
    phoneDisplay,
    handlePhoneChange,
    schedule,
    holidays,
    handleScheduleChange,
    handleToggleDay,
    handleAddHoliday,
    handleRemoveHoliday,
    scheduleLoading,
    utcLabel,
    callScheduleData,
    callHolidays,
    handleCallScheduleChange,
    handleCallToggleDay,
    handleCallAddHoliday,
    handleCallRemoveHoliday,
    callScheduleLoading,
    clinicName,
    scenarioType,
    setScenarioType,
    canViewCallPools,
    callsMaxTries,
    setCallsMaxTries,
    callsIntervalMinutes,
    setCallsIntervalMinutes,
    callsDisable,
    setCallsDisable,
    excludedDoctorIds,
    handleToggleSpecialization,
    handleToggleDoctor,
    doctorSearch,
    setDoctorSearch,
    specFilter,
    setSpecFilter,
    clinicSpecializations,
    filteredDoctors,
    interactionsDisabled,
  ]);

  return (
    <>
      {contextHolder}
      <Blocks
        topBlockClassName={styles.stickyTop}
        topBlock={
          <div className={blockStyles.detailHeader}>
            <div className={styles.topLeft}>
              <Button
                type="text"
                icon={<ArrowLeftOutlined />}
                onClick={handleBack}
                className={blockStyles.detailBackButton}
                disabled={interactionsDisabled}
              />
              <div className={styles.titleBlock}>
                {status.responseLoading ? (
                  <Skeleton.Input active style={{ width: 320, height: 28 }} />
                ) : (
                  <>
                    <Title level={4} className={styles.clinicTitle}>
                      {clinicName}
                    </Title>
                    <Text type="secondary" className={styles.clinicSubtitle}>
                      Настройка параметров звонков
                    </Text>
                  </>
                )}
              </div>
            </div>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={saving}
              disabled={!isDirty || interactionsDisabled}
              className={blockStyles.detailPrimaryButton}
            >
              Сохранить
            </Button>
          </div>
        }
        mainBlock={
          status.responseLoading ? (
            <div className={styles.skeletonWrap}>
              <Skeleton active paragraph={{ rows: 8 }} />
            </div>
          ) : (
            <div className={styles.mainBlock}>
              <Collapse
                activeKey={openPanels}
                onChange={(keys) => setOpenPanels(keys as string[])}
                className={styles.collapse}
                expandIconPosition="end"
                items={collapseItems}
              />
            </div>
          )
        }
      />
    </>
  );
});

ClinicSettingsDetail.displayName = 'ClinicSettingsDetail';
